import { animatePulse } from "@/src/utils/animation.utils";
import { ReactNode, useEffect, useRef } from "react";
import { Animated, StyleProp, View, ViewStyle } from "react-native";

type SkeletonPulseProps = Readonly<{
  children: ReactNode;
  /** Layout for the wrapper itself (e.g. `{ flex: 1 }`). */
  style?: StyleProp<ViewStyle>;
  testID?: string;
}>;

/**
 * Pulses everything inside it, for a loading placeholder.
 *
 * Deliberately takes `style`, not `className`: NativeWind doesn't style
 * Animated.View, so classes put on it were silently dropped — which is how
 * several skeletons lost their card background, border and spacing. Put the
 * card's classes on a plain View inside this instead.
 */
export const SkeletonPulse = ({ children, style, testID }: SkeletonPulseProps) => {
  const opacity = useRef(new Animated.Value(0.3)).current;

  useEffect(() => {
    const loop = animatePulse(opacity);
    return () => loop.stop();
  }, [opacity]);

  return (
    <Animated.View
      style={[{ opacity }, style]}
      testID={testID}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      {children}
    </Animated.View>
  );
};

/** One grey placeholder block. Size and shape come from `className`. */
export const Bone = ({ className = "" }: Readonly<{ className?: string }>) => (
  <View className={`bg-ash-3 rounded-md ${className}`} />
);
